import {makeAutoObservable, observable} from 'mobx'
import {observer} from 'mobx-react-lite'
import React, {CSSProperties, useEffect, useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {SearchUtil, SetUtil} from 'yougile-util'
import {Link} from '@components/common/button/button'
import {Highlight} from '@components/common/highlight/highlight'
import {TextInput} from '@components/common/text-input/text-input'
import {TreeNode} from '@components/common/tree/tree-node'
import {TriCheckbox} from '@components/common/tri-checkbox/tri-checkbox'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {createUseStore, useMemoOne} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import CloseIcon from './close.svg'
import OpenIcon from './open.svg'

const pageSize = 20

export interface TreeProps<T> {
  nodes: TreeNode<T>[]
  value: T[]
  onChange: (value: T[]) => void
  queryPlaceholder: string
  className?: string
  treeStyle?: CSSProperties
  oneSelectedItem?: boolean
  onClose?: () => void
  forceCustomScroll?: boolean
}

interface State<T> {
  query: string
  scrollOnTop: boolean
  openNodes: Set<T>
  hiddenNodes: Set<T>
  value: Set<T>
  paging: Map<T, number>
}

class Store<T> {
  state: State<T> = {
    query: '',
    scrollOnTop: true,
    value: new Set(),
    openNodes: new Set(),
    hiddenNodes: new Set(),
    paging: new Map()
  }
  props: TreeProps<T>
  constructor() {
    makeAutoObservable(this, {props: observable.shallow})
  }

  setProps(props: TreeProps<T>) {
    this.props = props
    this.state.value = new Set(props.value)
  }

  toggleOpen(value: T) {
    SetUtil.toggle(this.state.openNodes, value)
  }

  openPage(value: T) {
    this.state.paging.set(value, (this.state.paging.get(value) ?? 0) + 1)
  }

  getItemsCount(node: TreeNode<T>) {
    const page = this.state.paging.get(node.value) ?? 0
    const count = pageSize * (page + 1)
    return count
  }

  toggleValue(value: T) {
    if (this.props.oneSelectedItem) {
      const needAdd = !this.state.value.has(value)
      this.state.value.clear()
      if (needAdd) {
        this.state.value.add(value)
      }
    } else {
      SetUtil.toggle(this.state.value, value)
    }
    this.props.onChange([...this.state.value.values()])
  }

  openNodesWithQuery(node: TreeNode<T>): boolean {
    const hasQuery =
      node.selectable && SearchUtil.smartSearch(node.label, this.state.query)
    const childHasQuery = (node.children ?? [])
      .map(x => this.openNodesWithQuery(x))
      .some(x => x)
    const opened = hasQuery || childHasQuery
    if (opened) {
      this.state.openNodes.add(node.value)
    } else {
      this.state.hiddenNodes.add(node.value)
    }
    return opened
  }

  setScrollOnTop(onTop: boolean) {
    this.state.scrollOnTop = onTop
  }

  setQuery(query: string) {
    this.state.query = query
    this.state.openNodes.clear()
    this.state.hiddenNodes.clear()
    if (this.state.query) {
      this.props.nodes.forEach(x => this.openNodesWithQuery(x))
    }
  }
}

const [StoreContext, useStore] = createUseStore(Store)

const TwChildrenContainer = tw.div`
    ml-4
    pl-16
    border-0
    border-l
    border-light-old
    border-solid
`

const NodeLabel = observer(<T,>(props: {node: TreeNode<T>}) => {
  const [state, store] = useStore()
  const hasChildren = props.node.children?.length > 0
  const isClickable = hasChildren || props.node.selectable
  const showChildrenCount =
    !isClickable || (hasChildren && props.node.children.some(x => x.selectable))
  return (
    <>
      <div
        className={cn(
          isClickable && 'cursor-pointer',
          'whitespace-nowrap overflow-hidden text-ellipsis',
          props.node.className
        )}
        onClick={() => {
          if (props.node.selectable) {
            store.toggleValue(props.node.value)
          } else {
            store.toggleOpen(props.node.value)
          }
        }}
      >
        {props.node.selectable ? (
          <Highlight text={props.node.label} highlight={state.query} />
        ) : (
          props.node.label
        )}
      </div>
      {showChildrenCount && (
        <div className="ml-8 text-third-old text-12">
          ({props.node.children?.length ?? 0})
        </div>
      )}
    </>
  )
})

const NodeArrow = observer(<T,>(props: {node: TreeNode<T>}) => {
  const [state, store] = useStore()
  const opened = state.openNodes.has(props.node.value)
  const hasChildren = props.node.children && props.node.children.length > 0
  return (
    <div
      className={cn(
        'inline-flex cursor-pointer mr-4',
        !hasChildren && 'invisible'
      )}
      onClick={() => store.toggleOpen(props.node.value)}
    >
      {opened ? <OpenIcon /> : <CloseIcon />}
    </div>
  )
})

const NodeIcon = observer(<T,>(props: {node: TreeNode<T>}) => {
  const [state, store] = useStore()
  const hasChildren = props.node.children?.length > 0
  return (
    <>
      {props.node.selectable && (
        <TriCheckbox
          value={state.value.has(props.node.value)}
          onChange={() => store.toggleValue(props.node.value)}
          className="mr-2 shrink-0"
        />
      )}
      {props.node.icon && (
        <div
          className={cn(
            'inline-flex justify-center w-24 mr-4',
            hasChildren && 'cursor-pointer'
          )}
          onClick={() => store.toggleOpen(props.node.value)}
        >
          {props.node.icon}
        </div>
      )}
    </>
  )
})

const Node = observer(<T,>(props: {node: TreeNode<T>}) => {
  const [state] = useStore()
  const hidden = state.hiddenNodes.has(props.node.value)
  if (hidden) {
    return null
  }
  const opened = state.openNodes.has(props.node.value)
  const hasChildren = props.node.children?.length > 0

  return (
    <>
      <div className="h-32 flex items-center">
        <NodeArrow node={props.node} />
        <NodeIcon node={props.node} />
        <NodeLabel node={props.node} />
      </div>
      {opened && hasChildren && (
        <TwChildrenContainer>
          <Nodes parent={props.node} nodes={props.node.children} />
        </TwChildrenContainer>
      )}
    </>
  )
})

const Nodes = observer(
  <T,>(props: {nodes: TreeNode<T>[]; parent?: TreeNode<T>}) => {
    const [state, store] = useStore()
    const nodes = props.nodes.filter(x => !state.hiddenNodes.has(x.value))
    const nodesCount = props.parent
      ? store.getItemsCount(props.parent)
      : nodes.length
    const showNextPage = nodesCount < nodes.length
    return (
      <>
        {nodes.slice(0, nodesCount).map((x, i) => (
          <Node node={x} key={i} />
        ))}
        {showNextPage && <ShowMore value={props.parent.value} />}
      </>
    )
  }
)

const ShowMore = observer(<T,>(props: {value: T}) => {
  const [, store] = useStore()
  return (
    <div className="flex">
      <div className="h-32 w-48" />
      <Link className="ml-12 h-32" onClick={() => store.openPage(props.value)}>
        {Lang.msg.component.tree.showMore}
      </Link>
    </div>
  )
})

const SearchInput = observer(() => {
  const [state, store] = useStore()
  const ref = useRef<HTMLElement | undefined>()

  useEffect(() => {
    setTimeout(() => {
      ref.current?.focus()
    }, 100)
  }, [])

  return (
    <TextInput
      debounceMs={300}
      autoFocus={true}
      placeholder={store.props.queryPlaceholder}
      large={true}
      className="mb-12"
      searchIcon={true}
      value={state.query}
      onChange={x => store.setQuery(x)}
      onEscape={store.props.onClose}
      innerRef={ref}
    />
  )
})

const TwTreeContainer = tw.div`
    cursor-default
    text-14
    text-main-old
    leading-none
`

const Shadow = observer(() => {
  const [state] = useStore()
  const shadow =
    'radial-gradient(at top, var(--color-border-main-old), rgba(0, 0, 0, 0) 70%) 0 0/100% 8px no-repeat'
  return state.scrollOnTop ? null : (
    <div
      style={{background: shadow}}
      className="absolute z-10 bg-error-old h-16 top-0 left-0 right-0"
    ></div>
  )
})

export const Tree = <T,>(props: TreeProps<T>) => {
  const store = useMemoOne(() => new Store(), [props.nodes])
  store.setProps(props)
  return (
    <StoreContext.Provider value={store}>
      <div className={props.className}>
        <SearchInput />
        <div className="relative">
          <Shadow />
          <WithPerfectScrollbar
            onScrollY={x => store.setScrollOnTop(x.scrollTop === 0)}
            forceCustomScroll={props.forceCustomScroll}
          >
            <TwTreeContainer style={props.treeStyle}>
              <Nodes nodes={props.nodes} />
            </TwTreeContainer>
          </WithPerfectScrollbar>
        </div>
      </div>
    </StoreContext.Provider>
  )
}
